import { DateTime } from 'luxon'

import type { CostDetails } from '@/open-web/services/calculators/calculateContractTemplateCost'
import { getPriceBaseOnCustomerType } from '@/open-web/utils/getPriceBaseOnCustomerType'
import type { PriceDetails } from '@/shared/graphql/queries/queryTypes'
import type {
  ContractDiscountElement,
  CustomerType,
  TariffElement,
} from '@/shared/graphql/schema/commonBackend/graphql'
import type {
  PriceToStringFormatter,
  PriceToStringWithUnitFormatter,
} from '@/shared/hooks/usePriceToStringFormatter'
import type { EnrichedContractTemplate } from '@/shared/services/campaignDataResolver'
import { SalesCampaignMisconfigurationError } from '@/shared/utils/errorClasses'
import { isEnergyDiscountElement } from '@/shared/utils/tariffElementUtils'
import type { Theme } from '@/style/theme'

import type { PriceProps } from './types'
import { costToString } from '../PriceBreakdown/utils'

export const defineTheme = (colors: Theme['colors'], theme: PriceProps['theme'] = 'light') => {
  switch (theme) {
    case 'light':
      return {
        title: colors.textPrimary,
        price: colors.textPrimary,
        crossedPrice: colors.textSecondary,
        detailedPrice: colors.textPrimary,
        noPriceInfo: colors.textPositive,
        duration: colors.textPositive,
      }

    case 'dark':
      return {
        title: colors.textLightOnDark,
        price: colors.textLightOnDark,
        crossedPrice: colors.textLightOnDark,
        detailedPrice: colors.textLightOnDarkAccent,
        noPriceInfo: colors.textLightOnDarkAccent,
        duration: colors.textLightOnDarkAccent,
      }
  }
}

export const validateCustomerType = (contractTemplate: EnrichedContractTemplate) => {
  if (
    contractTemplate.customerType !== 'ENTERPRISE' &&
    contractTemplate.customerType !== 'PRIVATE'
  ) {
    const error = new SalesCampaignMisconfigurationError(
      `Contract template templateNo: ${contractTemplate.templateNo} has UNKNOWN customerType. Contract has bad configuration. Can not be sold!`,
      {
        templateNo: contractTemplate.templateNo,
        tariffNo: contractTemplate.tariffNo,
      },
    )

    return { error }
  }

  return { error: null }
}

export const getDiscountedPrice = (
  price: number,
  discountElement: ContractDiscountElement | undefined,
  customerType: string,
): number => {
  const discountElementPrice = discountElement?.prices
    ? getPriceBaseOnCustomerType(discountElement.prices[0], customerType)
    : 0
  return discountElement ? price + (discountElementPrice || 0) : price
}

export const getCurrentPrice = (prices?: PriceDetails[] | null) => {
  const today = DateTime.now()
  return prices?.find(
    (price) =>
      DateTime.fromISO(price.fromDate) <= today &&
      price.toDate &&
      DateTime.fromISO(price.toDate) > today,
  )
}

export const getFuturePrice = (prices?: PriceDetails[] | null) => {
  const today = DateTime.now()
  return prices?.find((price) => DateTime.fromISO(price.fromDate) > today) || null
}

export const getActiveOrFutureMonthlyFee = (monthlyFees?: TariffElement): PriceDetails | null => {
  if (!monthlyFees) {
    return null
  }
  const futureMonthlyFee = getFuturePrice(monthlyFees.prices ?? [])
  const currentMonthlyFee = getCurrentPrice(monthlyFees.prices ?? [])

  return futureMonthlyFee || currentMonthlyFee!
}

export const calculateDiscountPercent = (
  priceWithoutDiscountInclVat: number | null,
  priceWithDiscountInclVat: number | null,
): number | null => {
  if (priceWithoutDiscountInclVat == null || priceWithDiscountInclVat == null) {
    return null
  }

  // Full discount
  if (priceWithDiscountInclVat === 0) {
    return 100
  }

  const discountPercent =
    ((priceWithoutDiscountInclVat - priceWithDiscountInclVat) / priceWithoutDiscountInclVat) * 100

  return discountPercent
}

export const discountPercentToString = (
  priceWithoutDiscountInclVat: number | null,
  priceWithDiscountInclVat: number | null,
  transformer: PriceToStringFormatter,
) => {
  const discountPercent = calculateDiscountPercent(
    priceWithoutDiscountInclVat,
    priceWithDiscountInclVat,
  )

  if (discountPercent === 100) {
    return '100'
  }

  return transformer(discountPercent, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

export type PriceDetailsArgs = {
  isMonthlyFeeDiscounted: boolean
  isEnergyDiscounted: boolean
  monthlyFee: CostDetails | null
  monthlyFeeWithDiscount: CostDetails | null
  energy: CostDetails | null
  energyWithDiscount: CostDetails | null
  customerType: CustomerType
}

/**
 * Retrieves the prices with and without discounts based on the provided arguments.
 */
export const getPriceDetails = ({
  isMonthlyFeeDiscounted,
  isEnergyDiscounted,
  monthlyFee,
  monthlyFeeWithDiscount,
  energy,
  energyWithDiscount,
  customerType,
}: PriceDetailsArgs) => {
  if (isMonthlyFeeDiscounted) {
    return {
      priceWithoutDiscount: getPriceBaseOnCustomerType(monthlyFee, customerType),
      priceWithDiscount: getPriceBaseOnCustomerType(monthlyFeeWithDiscount, customerType),
    }
  }

  if (isEnergyDiscounted) {
    return {
      priceWithoutDiscount: getPriceBaseOnCustomerType(energy, customerType),
      priceWithDiscount: getPriceBaseOnCustomerType(energyWithDiscount, customerType),
    }
  }

  return {
    priceWithoutDiscount: null,
    priceWithDiscount: null,
  }
}

export const getEnergyPriceDetails = ({
  tariffElement,
  contractTemplate,
  formatPriceWithUnit,
}: {
  tariffElement: TariffElement | undefined
  contractTemplate: EnrichedContractTemplate
  formatPriceWithUnit: PriceToStringWithUnitFormatter
}) => {
  const customerType = contractTemplate.customerType
  const isVatIncluded = customerType === 'PRIVATE'

  const energyPriceElement = getCurrentPrice(tariffElement?.prices)
  const energyPriceDiscountElement = contractTemplate.discountElements.find((element) =>
    isEnergyDiscountElement(element.type),
  )
  const energyPrice = getPriceBaseOnCustomerType(energyPriceElement, customerType)
  const discountedEnergyPrice =
    energyPrice !== null
      ? getDiscountedPrice(energyPrice, energyPriceDiscountElement, customerType)
      : null

  const formattedEnergyPrice =
    energyPrice !== null
      ? costToString(
          { value: Number(energyPrice), priceUnit: tariffElement?.priceUnit },
          isVatIncluded,
          formatPriceWithUnit,
        )
      : null

  const formattedDiscountedEnergyPrice =
    discountedEnergyPrice !== null
      ? costToString(
          { value: Number(discountedEnergyPrice), priceUnit: tariffElement?.priceUnit },
          isVatIncluded,
          formatPriceWithUnit,
        )
      : null

  const energyPriceToDisplay = formattedDiscountedEnergyPrice ?? formattedEnergyPrice

  const duration = energyPriceDiscountElement?.duration
  const isEnergyDiscounted =
    Boolean(energyPriceDiscountElement) &&
    formattedDiscountedEnergyPrice &&
    formattedEnergyPrice &&
    formattedDiscountedEnergyPrice !== formattedEnergyPrice

  return {
    energyPrice,
    discountedEnergyPrice,
    formattedEnergyPrice,
    formattedDiscountedEnergyPrice,
    energyPriceToDisplay,
    duration,
    isEnergyDiscounted,
  }
}
